/**
 * fieldRules —— 校验规则引擎（纯函数）。
 *
 * 【本文件为何存在】同一份规则同时喂给两个消费面：
 *  - **实时标红**（输入期，`cellError` 的防抖可见链）
 *  - **整体校验**（提交/「校验」按钮，`useErrorLocator` 的 findFirstDoc）
 * 两处各写一份判定会导致「输入期提示」与「提交校验」漂移，故收敛到本文件。
 *
 * 【零 React 依赖】只有纯函数，不 import React，可被 jest 直接测
 * （见 `__tests__/fieldRules.test.ts`，断言值来自
 * `mixspec/changes/33313-合作内容表格引擎重构/fieldRules-行为断言清单.md`，**搬移前**按原实现手写）。
 *
 * 【规则语义】逐条对齐 `@bedrock/field-form` 所用的 async-validator。
 */
import type {
  ChildBase,
  FieldRule,
  FieldRuleType,
  FieldRules,
  FieldRuleValidatorFn,
  FieldRuleValidatorSync,
  SubColumnMeta,
} from './types';
// isRuleRender 是运行时判定函数（区分静态规则 / RuleRender），不能并入上面的 type-only import
import { isRuleRender } from './types';

/**
 * 必填校验的统一文案 —— 对齐折叠面板模式 SFormItem 的默认必填提示
 * （`SForm/components/SForm/SFormItem/index.tsx` 的「此项必填」）。
 * 表格模式与面板模式对同一份数据做校验，提示文案不应随展示形态而变。
 */
export const REQUIRED_MESSAGE = '此项必填';

/**
 * 子列校验规则：required 判空 + validator 判格式（如数值非负/小数位）。
 *
 * 无规则时返回空对象（引擎按无规则处理）。
 */
export const subColRules = (col: SubColumnMeta<ChildBase>): FieldRules => {
  const rs: FieldRule[] = [];
  if (col.required) rs.push({ required: true, message: REQUIRED_MESSAGE });
  if (col.validator) rs.push({ validator: col.validator });
  return rs.length ? { [col.field]: rs } : {};
};

/**
 * 该子项此子列当前是否**受控（可编辑）**—— 未声明 `SubColumnMeta.editable` 时恒为 true。
 *
 * **校验只覆盖受控字段**：只读展示 / disabled 控件用户改不了，对它报错会造成
 * 「提交被拦却无从修复」的死锁。实时标红与整体校验同源消费本判据。
 *
 * ⚠️ 判据是「控件受控性」而非「行/组是否可见」：`visibleRow` 管整组有无控件，
 * 本判据管同组内**个别字段**是否只读 —— 一行里部分字段只读、其余可编辑时，
 * 只有只读字段退出校验。
 */
export const subColEditable = <TChild,>(col: SubColumnMeta<TChild>, it: TChild): boolean =>
  !col.editable || col.editable(it);

// ============================================================
// 值判定
// ============================================================

/**
 * 「空值」判定。
 *
 * ⚠️ **只判 `undefined` / `null` / 空串** —— **不含空数组、不含 `false`、不含 `0`**。
 * 这条口径直接决定 `required` 的行为：`required: true` 遇到 `[]` **不会**报错；
 * 而 `sizeOfValue([]) === 0` 且 `!isEmpty([]) === true`，
 * 所以**空数组要靠 `min` 拦、`required` 拦不住**。
 *
 * （此前本函数的注释曾误称"含空数组"，与实现不符，已按实现修正。搬移时勿照旧注释"修正"实现。）
 */
export const isEmpty = (value: unknown): boolean => value === undefined || value === null || value === '';

/** async-validator 内置字符串类型的同步判定（engine 实际会用的子集） */
const RULE_TYPE_PATTERNS: Partial<Record<FieldRuleType, RegExp>> = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  url: /^https?:\/\/\S+$/i,
  hex: /^[0-9a-fA-F]+$/,
};

/** 值类型判定（对齐 async-validator 的 type 语义） */
export const matchRuleType = (type: FieldRuleType, v: unknown): boolean => {
  switch (type) {
    case 'string': return typeof v === 'string';
    case 'number': case 'float': return typeof v === 'number' && !Number.isNaN(v);
    case 'integer': return typeof v === 'number' && Number.isInteger(v);
    case 'boolean': return typeof v === 'boolean';
    case 'method': return typeof v === 'function';
    case 'regexp': return v instanceof RegExp;
    case 'object': return v !== null && typeof v === 'object' && !Array.isArray(v);
    case 'array': return Array.isArray(v);
    case 'date': return v instanceof Date;
    case 'enum': return true; // 白名单职责归 `enum` 字段，此处不做判定
    default: return RULE_TYPE_PATTERNS[type] ? RULE_TYPE_PATTERNS[type]!.test(String(v)) : true;
  }
};

/**
 * `len/min/max` 的度量值（对齐 async-validator 口径）：
 * 数组 → 元素个数；字符串 → 字符数；数值 → 自身；其余类型无度量（不做边界校验）。
 */
export const sizeOfValue = (v: unknown): number | undefined => {
  if (Array.isArray(v)) return v.length;
  if (typeof v === 'string') return v.length;
  if (typeof v === 'number') return v;
  return undefined;
};

// ============================================================
// 单条规则
// ============================================================

/**
 * 单条静态规则校验：返回错误信息（无错返回 undefined）。
 *
 * **判定顺序**（任一命中即返回，短路）：
 * `transform` → ①`validator` → ②`type` → ③`required` → ④`whitespace` → ⑤`enum` → ⑥`len/min/max` → ⑦`pattern`
 *
 * ⚠️ 顺序本身是契约：例如值同时不满足 `type` 与 `required` 时，返回的是 **type** 的错误。
 */
export const runOneRule = (r: FieldRule, rawValue: unknown, item: unknown, field: string): string | undefined => {
  // transform 结果同时供后续所有规则判定（对齐 async-validator）
  const value = r.transform ? r.transform(rawValue) : rawValue;
  const fallback = (suffix: string) => r.message ?? `${field}${suffix}`;

  // ① validator：按**形参个数**区分两种形态（3 参 = Form.Item 形态）
  if (r.validator) {
    if (r.validator.length >= 3) {
      let cbErr: string | undefined;
      let called = false;
      try {
        (r.validator as FieldRuleValidatorFn)(r, value, (e?: string) => { called = true; cbErr = e; });
      } catch (e) {
        // 校验器自身抛错：优先用规则 message，其次取异常信息
        return r.message ?? (e instanceof Error ? e.message : String(e));
      }
      // ⚠️ 仅当 callback **被调用过**才返回：未调用则继续走后续规则（不是"无错"）
      if (called) return cbErr || r.message;
    } else {
      // ⚠️ 同步分支**没有** try/catch —— 校验器抛异常会**向上传播**（与 ≥3 参分支不同）。
      // 这是既有行为，搬移时保持不变；勿"顺手"补 try/catch（那是行为变更）。
      const m = (r.validator as FieldRuleValidatorSync)(value, item);
      if (m) return m;
    }
  }

  // ② type 判定
  if (r.type && !matchRuleType(r.type, value)) return fallback('类型不正确');

  // ③ required
  if (r.required && isEmpty(value)) return r.message ?? `${field}必填`;

  // ④ whitespace：非空但全是空白（区别于 required 的「空」）
  if (r.whitespace && typeof value === 'string' && value !== '' && value.trim() === '') {
    return fallback('不能为纯空格');
  }

  // ⑤ enum 白名单（严格相等）
  if (r.enum && !r.enum.some((e) => e === value)) return fallback('不在允许范围内');

  // ⑥ len / min / max：仅在「有度量值且非空」时判定（空值职责归 required）
  const size = sizeOfValue(value);
  if (size !== undefined && !isEmpty(value)) {
    if (r.len !== undefined && size !== r.len) return fallback(`长度须为 ${r.len}`);
    if (r.min !== undefined && size < r.min) return fallback(`不能小于 ${r.min}`);
    if (r.max !== undefined && size > r.max) return fallback(`不能大于 ${r.max}`);
  }

  // ⑦ pattern（仅 string，沿用引擎既有行为）
  if (r.pattern && typeof value === 'string' && !r.pattern.test(value)) return fallback('格式不正确');

  return undefined;
};

/**
 * 值校验：逐条规则短路，返回**第一条**错误信息（无错返回 undefined）。
 *
 * `form` 用于展开 `FieldRuleRender`（动态规则）；不传则动态规则无法求值，将按静态规则尝试。
 * ⚠️ 与 `Form.Item` 的差异：后者返回**全部** errors，本引擎只需首个用于标红/定位，故短路。
 */
export const validateValue = (rules: FieldRules, field: string, value: unknown, item?: unknown, form?: unknown): string | undefined => {
  const rs = rules[field];
  if (!rs?.length) return undefined;
  for (const raw of rs) {
    const r = isRuleRender(raw) ? raw(form) : raw;
    const m = runOneRule(r, value, item, field);
    if (m) return m;
  }
  return undefined;
};
